import { groupIconForName } from './user-custom-field-types';

export const USER_FORM_SECTIONS = ['personal', 'security', 'account', 'extra'] as const;
export type UserFormSectionId = (typeof USER_FORM_SECTIONS)[number];
export type UserFormWidth = 'half' | 'full';

export type UserFormLayoutItem = {
  id: string;
  section: UserFormSectionId;
  formWidth: UserFormWidth;
};

export const USER_FORM_BUILTIN_IDS = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'password',
  'passwordConfirmation',
  'status',
  'roles',
] as const;

export type UserFormBuiltinId = (typeof USER_FORM_BUILTIN_IDS)[number];

const builtinIds = new Set<string>(USER_FORM_BUILTIN_IDS);

export function isUserFormBuiltin(id: string): id is UserFormBuiltinId {
  return builtinIds.has(id);
}

export function layoutItemsForSection(items: UserFormLayoutItem[], section: UserFormSectionId): UserFormLayoutItem[] {
  return items.filter((item) => item.section === section);
}

/** Campos de la sección extra que no pertenecen a un grupo de campos personalizados. */
export const UNGROUPED_FORM_FIELDS = 'Campos Personalizados';

export function formGroupIcon(groupName: string): string {
  if (groupName === UNGROUPED_FORM_FIELDS) return 'edit_note';
  return groupIconForName(groupName);
}

/**
 * Parte la sección extra del formulario en los mismos grupos que la ficha del usuario.
 * El orden dentro de cada grupo es el del layout; el orden de los grupos sigue `preferredOrder`.
 */
export function layoutGroupsForExtra(
  items: readonly UserFormLayoutItem[],
  groupNameOf: (id: string) => string | null | undefined,
  preferredOrder: readonly string[] = [],
): Array<{ groupName: string; items: UserFormLayoutItem[] }> {
  const buckets = new Map<string, UserFormLayoutItem[]>();
  for (const item of items) {
    if (item.section !== 'extra') continue;
    const name = groupNameOf(item.id)?.trim() || UNGROUPED_FORM_FIELDS;
    const bucket = buckets.get(name);
    if (bucket) bucket.push(item);
    else buckets.set(name, [item]);
  }
  if (buckets.size === 0) return [];

  const names: string[] = [];
  for (const name of preferredOrder) {
    const trimmed = name.trim();
    if (!trimmed || names.includes(trimmed) || !buckets.has(trimmed)) continue;
    names.push(trimmed);
  }
  for (const name of buckets.keys()) {
    if (name !== UNGROUPED_FORM_FIELDS && !names.includes(name)) names.push(name);
  }
  if (buckets.has(UNGROUPED_FORM_FIELDS)) names.push(UNGROUPED_FORM_FIELDS);
  return names.map((groupName) => ({ groupName, items: buckets.get(groupName) ?? [] }));
}

export function formColumnSpan(formWidth: UserFormWidth, wide: 'md' | 'lg'): string | undefined {
  if (formWidth !== 'full') return undefined;
  return wide === 'md' ? 'md:col-span-2' : 'lg:col-span-2';
}

/** Hueco de una columna que deja un campo a mitad cuando el siguiente ocupa la fila. */
export type UserFormLayoutGap = {
  id: string;
  beforeId: string | null;
};

export function userFormLayoutGaps(items: UserFormLayoutItem[]): UserFormLayoutGap[] {
  const gaps: UserFormLayoutGap[] = [];
  let column = 0;
  for (const item of items) {
    const span = item.formWidth === 'full' ? 2 : 1;
    if (column + span > 2) {
      gaps.push({ id: `before:${item.id}`, beforeId: item.id });
      column = 0;
    }
    column = (column + span) % 2;
  }
  if (column === 1) gaps.push({ id: 'end', beforeId: null });
  return gaps;
}

export function sameUserFormLayout(current: UserFormLayoutItem[], saved: UserFormLayoutItem[]): boolean {
  const key = (items: UserFormLayoutItem[]) => items.map((item) => `${item.id}:${item.section}:${item.formWidth}`).join('\0');
  return key(current) === key(saved);
}

/**
 * `before`: inserta el campo antes del destino (hueco vacío).
 * `slot`: el campo ocupa el lugar del destino y ese destino se corre.
 * En una fila de dos columnas, insertar siempre "antes" deja quieto el
 * recuadro de la derecha cuando el origen es el de la izquierda.
 */
export function moveUserFormLayoutItem(
  items: UserFormLayoutItem[],
  activeId: string,
  overId: string | null,
  section: UserFormSectionId,
  placement: 'before' | 'slot' = 'before',
): UserFormLayoutItem[] {
  const from = items.findIndex((item) => item.id === activeId);
  const current = items[from];
  if (!current) return items;
  if (placement === 'slot' && overId === activeId && current.section === section) return items;
  const copy = items.filter((item) => item.id !== activeId);
  const moved: UserFormLayoutItem = { ...current, section };
  if (!overId) {
    let last = -1;
    for (let index = 0; index < copy.length; index += 1) {
      if (copy[index]?.section === section) last = index;
    }
    copy.splice(last + 1, 0, moved);
    return copy;
  }
  let to = copy.findIndex((item) => item.id === overId);
  if (to < 0) return items;
  if (placement === 'slot') {
    const overIndex = items.findIndex((item) => item.id === overId);
    if (from < overIndex) to += 1;
  }
  copy.splice(to, 0, moved);
  return copy;
}

export function fallbackUserFormLayout(
  personal: Array<{ id: string; formWidth: UserFormWidth }>,
  extra: Array<{ id: string; formWidth: UserFormWidth }>,
): UserFormLayoutItem[] {
  return [
    { id: 'firstName', section: 'personal', formWidth: 'half' },
    { id: 'lastName', section: 'personal', formWidth: 'half' },
    { id: 'email', section: 'personal', formWidth: 'half' },
    { id: 'phone', section: 'personal', formWidth: 'half' },
    ...personal.map((field) => ({ id: field.id, section: 'personal' as const, formWidth: field.formWidth })),
    { id: 'password', section: 'security', formWidth: 'half' },
    { id: 'passwordConfirmation', section: 'security', formWidth: 'half' },
    { id: 'status', section: 'account', formWidth: 'half' },
    { id: 'roles', section: 'account', formWidth: 'half' },
    ...extra.map((field) => ({ id: field.id, section: 'extra' as const, formWidth: field.formWidth })),
  ];
}
